import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import { IconEdit, IconTrash, IconPlus, IconImage } from '../components/Icons';
import './Admin.css';

const categories    = ['journal', 'book', 'report'];
const categoryLabel = { 
  journal: 'Journal Articles', 
  book: 'Books and Monographs', 
  report: 'Reports and Documents' 
};

const emptyForm = { title: '', category: 'journal', journal: '', publisher: '', outlet: '', bookTitle: '', year: '', volume: '', issue: '', pages: '', abstract: '', doi: '', url: '', tags: '' };

export default function AdminPublications() {
  const { authFetch } = useAdminAuth();
  const [pubs,      setPubs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [file,      setFile]      = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [isOtherTag, setIsOtherTag] = useState(false);
  const [customTag,  setCustomTag]  = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadPubs = () => {
    setLoading(true);
    fetch('/api/publications')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      })
      .then(data => { 
        if (data && data.items) {
          setPubs(data.items);
        } else if (Array.isArray(data)) {
          setPubs(data);
        } else {
          setPubs([]);
        }
        setLoading(false); 
      })
      .catch((err) => {
        console.error("Fetch publications error in admin:", err);
        setPubs([]);
        setLoading(false);
      });
  };

  useEffect(() => { loadPubs(); }, []);

  const openNew = () => { setForm(emptyForm); setFile(null); setCoverFile(null); setEditId(null); setError(''); setShowModal(true); };
  const openEdit = (pub) => {
    setForm({ ...emptyForm, ...pub, tags: (pub.tags||[]).join(', ') });
    setFile(null);
    setCoverFile(null);
    setEditId(pub.id); 
    setError(''); 
    setIsOtherTag(false);
    setCustomTag('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    
    const formData = new FormData();
    // Fields first
    Object.keys(form).forEach(key => {
      if (key === 'year' && form.year) {
        formData.append('year', String(form.year));
      } else if (form[key] !== null && form[key] !== undefined) {
        formData.append(key, form[key]);
      }
    });

    if (isOtherTag && customTag.trim()) {
      const currentTags = (form.tags || '').split(',').map(t => t.trim()).filter(Boolean);
      const newTag = customTag.trim().toLowerCase();
      if (!currentTags.includes(newTag)) {
        formData.set('tags', [...currentTags, newTag].join(', '));
      }
    }

    // Files last
    if (file) formData.append('file', file);
    if (coverFile) formData.append('cover', coverFile);

    try {
      const url    = editId ? `/api/publications/${editId}` : '/api/publications';
      const method = 'POST'; // Use POST for both create and update for PHP compatibility with files
      
      if (isOtherTag && customTag.trim()) {
        const currentTags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
        const newTag = customTag.trim().toLowerCase();
        if (!currentTags.includes(newTag)) {
           formData.set('tags', [...currentTags, newTag].join(', '));
        }
      }

      const res    = await authFetch(url, { method, body: formData });
      if (!res.ok) throw new Error();
      setShowModal(false);
      loadPubs();
    } catch { setError('Save failed. Please try again.'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this publication? This cannot be undone.')) return;
    await authFetch(`/api/publications/${id}`, { method: 'DELETE' });
    loadPubs();
  };

  const filteredPubs = pubs.filter(pub => {
    const query = searchTerm.toLowerCase();
    const title = (pub.title || '').toLowerCase();
    const source = (pub.journal || pub.publisher || pub.outlet || pub.bookTitle || '').toLowerCase();
    const tags = (pub.tags || []).join(' ').toLowerCase();
    const cat = (pub.category || '').toLowerCase();
    
    return (
      title.includes(query) ||
      source.includes(query) ||
      tags.includes(query) ||
      (pub.year && String(pub.year).includes(query)) ||
      cat.includes(query)
    );
  });

  const f = (key, label, opts={}) => (
    <div className="admin-field" key={key}>
      <label>{label}</label>
      {opts.textarea
        ? <textarea rows={3} value={form[key]||''} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} placeholder={opts.placeholder||''} />
        : <input type={opts.type||'text'} value={form[key]||''} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} placeholder={opts.placeholder||''} />
      }
    </div>
  );

  return (
    <AdminLayout title="Publications" page="publications">
      <div className="admin-content">
        <div className="admin-page-header">
          <h2>Publications</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="admin-search-bar" style={{ position: 'relative' }}>
              <input 
                type="text" 
                placeholder="Search publications..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                autoComplete="off"
                style={{
                  padding: '0.6rem 1rem',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-dark)',
                  fontSize: '0.85rem',
                  width: '240px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-light)',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >✕</button>
              )}
            </div>
            <button id="add-publication-btn" className="btn btn-primary btn-sm" onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <IconPlus size={16} /> Add Publication
            </button>
          </div>
        </div>

        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Year</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPubs.map(pub => (
                  <tr key={pub.id}>
                    <td className="admin-table__title">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {pub.coverUrl && <span title="Has Cover Image" style={{ display: 'flex', alignItems: 'center' }} aria-label="Has Cover"><IconImage size={16} color="var(--navy)" /></span>}
                        <span style={{ color: pub.title ? 'inherit' : 'var(--text-light)', fontStyle: pub.title ? 'normal' : 'italic' }}>
                          {pub.title || '(No title provided)'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-navy">
                        {categoryLabel[pub.category] || pub.category || 'Other'}
                      </span>
                    </td>
                    <td className="admin-table__year">{pub.year || pub.date || '—'}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                      {pub.journal || pub.publisher || pub.outlet || pub.bookTitle || '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(pub)} title="Edit"><IconEdit size={14} /></button>
                        <button className="btn btn-danger btn-sm"  onClick={() => handleDelete(pub.id)} title="Delete"><IconTrash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal" style={{ maxWidth: 640 }}>
            <h3>{editId ? 'Edit Publication' : 'Add Publication'}</h3>
            <div className="admin-form">
              {f('title', 'Title *')}
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Type</label>
                  <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                    {categories.map(c => <option key={c} value={c}>{categoryLabel[c]}</option>)}
                  </select>
                </div>
                {f('year', 'Year', { type: 'number', placeholder: '2024' })}
              </div>
              {form.category === 'journal' && f('journal', 'Journal Name')}
              {form.category === 'book'    && <div className="admin-form-row">{f('bookTitle', 'Series/Book Title (optional)')}{f('publisher', 'Publisher')}</div>}
              {form.category === 'report'  && <div className="admin-form-row">{f('publisher', 'Publisher/Institution')}{f('outlet', 'Media Outlet/Platform (optional)')}</div>}
              {form.category === 'journal' && <div className="admin-form-row">{f('volume', 'Volume')}{f('issue', 'Issue')}{f('pages', 'Pages')}</div>}
              {f('abstract', 'Abstract / Description', { textarea: true })}
              <div className="admin-form-row">
                {f('doi', 'DOI (if any)', { placeholder: '10.xxxx/xxxx' })}
                {f('url', 'External Link / URL')}
              </div>
              <div className="admin-field">
                <label>Tags</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  {(form.tags || '').split(',').map(t => t.trim()).filter(Boolean).map(t => (
                    <span key={t} className="badge badge-navy" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {t}
                      <button 
                        type="button" 
                        onClick={() => {
                          const updated = form.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== t).join(', ');
                          setForm(f => ({ ...f, tags: updated }));
                        }}
                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}
                      >✕</button>
                    </span>
                  ))}
                </div>
                <div className="admin-form-row" style={{ gap: '0.5rem', marginBottom: 0 }}>
                  <select 
                    value={isOtherTag ? 'other' : ''} 
                    onChange={e => {
                      if (e.target.value === 'other') {
                        setIsOtherTag(true);
                      } else if (e.target.value) {
                        const currentTags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
                        if (!currentTags.includes(e.target.value)) {
                          setForm(f => ({ ...f, tags: [...currentTags, e.target.value].join(', ') }));
                        }
                        setIsOtherTag(false);
                      }
                    }}
                    style={{ flex: 1 }}
                  >
                    <option value="" style={{ color: '#94a3b8', fontStyle: 'italic' }}>Select Existing Tag</option>
                    {Array.from(new Set(pubs.flatMap(p => p.tags || []))).sort().map(t => (
                      <option key={t} value={t} style={{ color: 'white' }}>{t}</option>
                    ))}
                    <option value="other" style={{ color: '#94a3b8', fontStyle: 'italic' }}>Other (Enter New)</option>
                  </select>
                  {isOtherTag && (
                    <div style={{ flex: 1, display: 'flex', gap: '0.4rem' }}>
                      <input 
                        value={customTag} 
                        onChange={e => setCustomTag(e.target.value)} 
                        placeholder="New tag..." 
                        autoFocus 
                        style={{ flex: 1 }}
                      />
                      <button 
                        className="btn btn-navy btn-sm"
                        onClick={() => {
                          if (customTag.trim()) {
                            const currentTags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
                            const newTag = customTag.trim().toLowerCase();
                            if (!currentTags.includes(newTag)) {
                              setForm(f => ({ ...f, tags: [...currentTags, newTag].join(', ') }));
                            }
                            setCustomTag('');
                            setIsOtherTag(false);
                          }
                        }}
                      >Add</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Document File (PDF/DOC)</label>
                  <div className="admin-file-picker">
                    <label className="admin-file-picker__btn">
                      Choose File
                      <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])} />
                    </label>
                    <span className="admin-file-picker__name">
                      {file ? file.name : (editId && form.fileUrl ? form.fileUrl.split('/').pop() : 'No file chosen')}
                    </span>
                  </div>
                </div>
                <div className="admin-field">
                  <label>Cover Image (JPG/PNG)</label>
                  <div className="admin-file-picker">
                    <label className="admin-file-picker__btn">
                      Choose Image
                      <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files[0])} />
                    </label>
                    <span className="admin-file-picker__name">
                      {coverFile ? coverFile.name : (editId && form.coverUrl ? form.coverUrl.split('/').pop() : 'No file chosen')}
                    </span>
                  </div>
                </div>
              </div>
              {error && <p style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</p>}
            </div>
            <div className="admin-modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
